/*
 * Journal article chrome (Fix B) — scroll progress, sticky chapter bar and
 * chapter-index scroll-spy + smooth jumps. Pure vanilla JS, progressive
 * enhancement: the index links are real in-page anchors that work without
 * this script, and the FAQ +/− rotation is CSS-only (details[open]).
 *
 * DOM contract with sections/article-editorial.liquid:
 *   .article-editorial__progress-fill  — scaleX progress fill (optional)
 *   .article-editorial__bar            — sticky chapter bar (optional)
 *   [data-bar-num] / [data-bar-title]  — bar text, prefilled with the article title
 *   [data-contents-jump]               — bar link to #contents
 *   [data-index-link]                  — chapter index rows (href="#<chapter id>")
 *   [data-chapter]                     — chapter openers carrying
 *                                        data-chapter-num / data-chapter-title
 */
(() => {
  'use strict';

  const root = document.querySelector('.article-editorial');
  if (!root || root.dataset.journalInit) return;
  root.dataset.journalInit = '1';

  const progressFill = root.querySelector('.article-editorial__progress-fill');
  const bar = root.querySelector('.article-editorial__bar');
  const barNum = root.querySelector('[data-bar-num]');
  const barTitle = root.querySelector('[data-bar-title]');
  const masthead = root.querySelector('.article-editorial__header');
  const content = root.querySelector('.article-editorial__article') || root;
  const indexLinks = Array.from(root.querySelectorAll('[data-index-link]'));
  const chapters = Array.from(root.querySelectorAll('[data-chapter]'));

  // Active = last chapter whose opener has cleared this line. Must stay larger
  // than the 96px scroll-margin-top in the CSS, or a completed jump would stop
  // with its own chapter still "above" the spy line and mark the previous one.
  const SPY_OFFSET = 160;
  const BAR_REVEAL_FALLBACK = 520;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const clamp01 = (n) => Math.min(1, Math.max(0, n));
  const scrollTop = () => window.pageYOffset || document.documentElement.scrollTop || 0;

  // Reveal the bar once the masthead has scrolled past, wherever that falls
  // for this title length and viewport; a fixed pixel constant is only the
  // fallback when there is no masthead to measure.
  let barRevealY = BAR_REVEAL_FALLBACK;
  const measure = () => {
    if (!masthead) return;
    const rect = masthead.getBoundingClientRect();
    barRevealY = rect.top + scrollTop() + rect.height;
  };

  let lastActive = null;
  const setActive = (chapter) => {
    const id = chapter ? chapter.id : null;
    if (id === lastActive) return;
    lastActive = id;

    if (chapter && barNum) barNum.textContent = chapter.dataset.chapterNum || '';
    if (chapter && barTitle) barTitle.textContent = chapter.dataset.chapterTitle || '';

    indexLinks.forEach((link) => {
      const match = id !== null && link.getAttribute('href') === `#${id}`;
      link.classList.toggle('is-active', match);
      if (match) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  let ticking = false;
  const update = () => {
    ticking = false;
    const y = scrollTop();

    if (progressFill) {
      const start = content.getBoundingClientRect().top + y;
      const span = content.offsetHeight - window.innerHeight;
      progressFill.style.transform = `scaleX(${span > 0 ? clamp01((y - start) / span) : 0})`;
    }

    if (bar) {
      bar.classList.toggle('is-visible', y > barRevealY);
    }

    if (chapters.length) {
      let active = chapters[0];
      chapters.forEach((chapter) => {
        if (chapter.getBoundingClientRect().top - SPY_OFFSET <= 0) active = chapter;
      });
      setActive(active);
    }
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  };

  const onResize = () => {
    measure();
    onScroll();
  };

  const onJumpClick = (event) => {
    const link = event.currentTarget;
    const href = link.getAttribute('href') || '';
    if (href.charAt(0) !== '#') return;
    const target = document.getElementById(href.slice(1));
    if (!target) return;

    event.preventDefault();
    // CSS scroll-margin-top owns the landing offset — no offset math here.
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  };

  indexLinks.forEach((link) => link.addEventListener('click', onJumpClick));
  root.querySelectorAll('[data-contents-jump]').forEach((link) => link.addEventListener('click', onJumpClick));
  root.querySelectorAll('[data-video-consent-renew]').forEach((button) =>
    button.addEventListener('click', () => {
      if (typeof Cookiebot !== 'undefined') Cookiebot.renew();
    })
  );
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });
  measure();
  update();
})();
